import React, { useState, useEffect, useCallback, useRef } from 'react';
import partnerSyncTemplatesService from '../services/partnerSyncTemplatesService';
import syncTemplatesService from '../services/syncTemplatesService';
import accountService from '../services/accountService';
import hotelService from '../services/hotelService';
import customCodeService from '../services/customCodeService';
import CustomCodeAssistant from './CustomCodeAssistant';
import CustomCodeTester from './shared/CustomCodeTester';
import SyncTemplateGraphic from './SyncTemplateGraphic';
import './PartnerSyncTemplates.css';

// ─── Cards view ───────────────────────────────────────────────────────────────

const PlatformCard = ({ platform, partnerTemplates, onClick }) => {
    const mine = partnerTemplates.filter(t => t.synctemplatesid === platform.id);
    const defaultOne = mine.find(t => Number(t.isDefault) === 1);

    return (
        <div className="pst-card" onClick={onClick}>
            <SyncTemplateGraphic template={platform} />
            <div className="pst-card-body">
                <div className="pst-card-title">{platform.name}</div>
                {platform.code && <div className="pst-card-code">{platform.code}</div>}
                <div className="pst-card-stats">
                    <span className="pst-card-count">
                        {mine.length === 0
                            ? 'No partner templates'
                            : `${mine.length} partner template${mine.length !== 1 ? 's' : ''}`}
                    </span>
                    {defaultOne ? (
                        <span className="pst-card-default">
                            <span className="pst-default-dot" />
                            Default: {defaultOne.name}
                        </span>
                    ) : (
                        <span className="pst-card-platform-default">
                            Using platform tasks
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

// Example code inserted by the Initialize button when writing a new custom code snippet.
// The snippet only runs for records matching the Scope selected above (or every scope when Scope
// is left blank), so the code itself doesn't need to check the scope.
const CUSTOM_CODE_SKELETON = `// Custom code — runs on each matching record for this platform in the sync pipeline.
//
//   payload : the record JSON being sent to the next step. Modify it and return it.
//   context : { partnerId, chainId, hotelId } — who this record is being synced for.
//
// Return the (modified) payload. Return it unchanged to pass through.
function transform(payload, context) {
    // Change a value deep in a nested structure. Guard each level so a missing field
    // never throws — a snippet that throws will error the record (example path shown).
    const name = payload.profileDetails
        && payload.profileDetails.customer
        && payload.profileDetails.customer.personName
        && payload.profileDetails.customer.personName[0];
    if (name) {
        name.nameType = 'Primary';
    }

    return payload;
}
`;

const PartnerSyncTemplates = ({ partnerId }) => {
    const [platformTemplates, setPlatformTemplates] = useState([]);
    const [partnerTemplates, setPartnerTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Transient, dismissible message for actions the server refused (e.g. default vs shares). Separate
    // from `error`, which replaces the whole view — a rejected click should not blank the screen.
    const [notice, setNotice] = useState(null);

    // Drill-down state (3 levels: cards → list → detail)
    const [activePlatform, setActivePlatform] = useState(null);   // level 2
    const [activeTemplate, setActiveTemplate] = useState(null);   // level 3 — full partner template object
    const [platformDetailTab, setPlatformDetailTab] = useState('templates'); // level 2 tabs: 'templates' | 'custom'

    // Custom Code tab (per partner + platform)
    const [customCodes, setCustomCodes] = useState([]);
    const [codeScopeTypes, setCodeScopeTypes] = useState([]); // [{ scopeId, scopeType }] for this template
    const [customCodesLoading, setCustomCodesLoading] = useState(false);
    const [codeModal, setCodeModal] = useState(null); // null | { id?, name, code, accountId, hotelIds[] }
    // Narrows the hotel list in the editor. A chain can hold hundreds of properties, and a checkbox list
    // that long is only usable if you can find the three you want.
    const [codeHotelSearch, setCodeHotelSearch] = useState('');
    const [codeSaving, setCodeSaving] = useState(false);
    const [codeError, setCodeError] = useState(null);
    const codeNameRef = useRef(null);
    const [modalError, setModalError] = useState(null);
    const modalNameRef = useRef(null);
    const [codeAccounts, setCodeAccounts] = useState([]);
    const [codeHotels, setCodeHotels] = useState([]);

    // Test harness: paste a payload, run the snippet against it, see what it changed.
    const [testPayload, setTestPayload] = useState('');
    const [testOpen, setTestOpen] = useState(false);
    const [assistOpen, setAssistOpen] = useState(false);

    // Task exclusions
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [togglingTaskId, setTogglingTaskId] = useState(null);

    // Create / rename modal
    const [modalMode, setModalMode] = useState(null); // 'new' | id
    const [modalName, setModalName] = useState('');
    const [modalSaving, setModalSaving] = useState(false);

    // Default saving
    const [settingDefaultId, setSettingDefaultId] = useState(null);

    // Shares
    const [shares, setShares] = useState({}); // { [templateId]: share[] }
    const [shareModal, setShareModal] = useState(null); // { templateId }
    const [shareType, setShareType] = useState('account'); // 'account' | 'hotel'
    const [shareAccounts, setShareAccounts] = useState([]);
    const [shareHotels, setShareHotels] = useState([]);
    const [shareAccountPick, setShareAccountPick] = useState(null); // { id, name }
    const [shareHotelPick, setShareHotelPick] = useState(null);   // { id, name }
    const [shareChainSearch, setShareChainSearch] = useState('');
    const [shareHotelSearch, setShareHotelSearch] = useState('');
    const [shareHotelsLoading, setShareHotelsLoading] = useState(false);
    const [shareSaving, setShareSaving] = useState(false);

    // Delete confirm
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        if (!partnerId) return;
        Promise.all([
            syncTemplatesService.listTemplates(),
            partnerSyncTemplatesService.list(partnerId),
        ])
            .then(([ptRows, stRows]) => {
                setPlatformTemplates((ptRows || []).filter(t => t.status === 'ACTIVE'));
                setPartnerTemplates(stRows || []);
            })
            .catch(err => setError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [partnerId]);

    const refreshPartnerTemplates = useCallback(async () => {
        const rows = await partnerSyncTemplatesService.list(partnerId);
        setPartnerTemplates(rows || []);
    }, [partnerId]);

    const loadTasks = useCallback(async (id) => {
        setTasksLoading(true);
        try {
            const rows = await partnerSyncTemplatesService.getTasks(id);
            setTasks(rows || []);
        } catch {
            setTasks([]);
        } finally {
            setTasksLoading(false);
        }
    }, []);

    const loadCustomCodes = useCallback(async (platform) => {
        if (!partnerId || !platform) return;
        setCustomCodesLoading(true);
        try {
            const res = await customCodeService.list(partnerId, platform.id);
            // New shape: { snippets, scopeTypes }. Fall back to a bare array for safety.
            setCustomCodes(Array.isArray(res) ? res : (res.snippets || []));
            setCodeScopeTypes(Array.isArray(res) ? [] : (res.scopeTypes || []));
        } catch (e) {
            console.error('Failed to load custom code', e);
            setCustomCodes([]);
            setCodeScopeTypes([]);
        } finally {
            setCustomCodesLoading(false);
        }
    }, [partnerId]);

    const loadCodeAccounts = async () => {
        try {
            const res = await accountService.getAllAccounts({ partnerid: partnerId, activeOnly: true });
            setCodeAccounts(res.accounts || res || []);
        } catch { setCodeAccounts([]); }
    };

    const loadCodeHotels = async (accountId) => {
        if (!accountId) { setCodeHotels([]); return; }
        try {
            const res = await hotelService.getAllHotels({ accountid: accountId });
            setCodeHotels(res.hotels || res || []);
        } catch { setCodeHotels([]); }
    };

    // Scope ids for a snippet, as strings for comparison against checkbox values.
    //
    // Reads the V409 list (scope_ids, a GROUP_CONCAT) and falls back to the superseded single-column
    // syncscopeid so a snippet saved before the migration still opens with its scope selected.
    const parseScopeIds = (c) => {
        if (c && c.scope_ids != null && String(c.scope_ids) !== '') return String(c.scope_ids).split(',').map(x => x.trim()).filter(Boolean);
        if (c && c.syncscopeid) return [String(c.syncscopeid)];
        return [];
    };

    // The stored hotels, from the child table (V427). `hotel_ids` is a GROUP_CONCAT, and hotel_id is the
    // superseded single column — read only as a fallback so a row written before the migration, or by an
    // older API build, still opens with its target selected.
    const parseHotelIds = (c) => {
        if (c.hotel_ids) return String(c.hotel_ids).split(',').map(x => x.trim()).filter(Boolean);
        return c.hotel_id ? [String(c.hotel_id)] : [];
    };

    const handleAddCode = () => {
        // New snippets open INACTIVE: they are written against live traffic, so switching one on is a
        // deliberate act rather than a side effect of saving. Existing snippets keep whatever they have.
        setCodeModal({ name: '', code: '', accountId: '', hotelIds: [], syncScopeIds: [], active: false });
        setCodeHotels([]);
        setCodeHotelSearch('');
        loadCodeAccounts();
    };

    const handleEditCode = (c) => {
        // A snippet targets a hotel XOR a chain, so a hotel-scoped snippet has account_id NULL. The hotel
        // dropdown lists the hotels OF THE SELECTED CHAIN, so opening such a snippet with no chain left it
        // with nothing to select from: Target Chain read "— select chain —" and Target Hotel "— whole chain —",
        // which read as "nothing was saved" even though the row was stored correctly. Fall back to the
        // hotel's owning chain (hotel_account_id, derived by the API) so the chain shows, its hotels load
        // and the stored hotel preselects. Saving still nulls the chain whenever a hotel is set, so the
        // stored row keeps its single target.
        const chainId = c.account_id || c.hotel_account_id || c.hotels_account_id || '';
        setCodeModal({ id: c.id, name: c.name, code: c.code || '', accountId: chainId,
                       hotelIds: parseHotelIds(c),
                       syncScopeIds: parseScopeIds(c), active: String(c.active || 'Y').toUpperCase() !== 'N' });
        setCodeHotelSearch('');
        loadCodeAccounts();
        loadCodeHotels(chainId);
    };

    // Duplicate a snippet: open the editor pre-filled from the original, with no id so saving CREATES.
    //
    // It deliberately does NOT copy straight to the server. A duplicate is almost always made in order to
    // retarget it — same logic, different chain or hotel — so the point of the copy is the edit that
    // follows. Opening the form lets that happen before the row exists, rather than creating a second
    // snippet aimed at the SAME target and relying on someone to go back and fix it.
    //
    // INACTIVE regardless of the original's state, for the same reason handleAddCode starts inactive:
    // these run against live traffic, and duplicating an active snippet would put a second copy of it
    // into the pipeline the moment it saved. Switching it on stays a deliberate act.
    //
    // The list rows carry the full body (cc.code is in the API's column list), so nothing is lost here.
    const handleDuplicateCode = (c) => {
        const chainId = c.account_id || c.hotel_account_id || c.hotels_account_id || '';
        setCodeModal({
            name: `${c.name} (copy)`,
            code: c.code || '',
            accountId: chainId,
            hotelIds: parseHotelIds(c),
            syncScopeIds: parseScopeIds(c),
            active: false,
        });
        setCodeHotelSearch('');
        loadCodeAccounts();
        loadCodeHotels(chainId);
    };

    const handleCodeChainChange = (accountId) => {
        // Chain change resets the hotel selection and reloads that chain's hotels.
        setCodeModal({ ...codeModal, accountId, hotelIds: [] });
        setCodeHotelSearch('');
        loadCodeHotels(accountId);
    };

    // Run the snippet in the browser against a pasted payload and diff the result.
    //
    const handleSaveCode = async () => {
        if (!codeModal) return;
        // A DISABLED SAVE BUTTON EXPLAINS NOTHING. This used to `return` silently while the button sat
        // greyed out, so a snippet with no name looked identical to a save that had failed somewhere in
        // the stack — the button did nothing and said nothing, and the list behind it never changed.
        // Better to accept the click and name the missing field.
        if (!codeModal.name.trim()) {
            setCodeError('Give the snippet a name before saving.');
            if (codeNameRef.current) codeNameRef.current.focus();
            return;
        }
        setCodeSaving(true);
        setCodeError(null);
        // A snippet targets HOTELS (preferred) or a chain; naming any hotel wins over the chain, and the
        // chain is nulled so the row keeps one meaning. The runtime matcher relies on that: a snippet
        // carrying both would match at chain level too and run on every other property in the chain.
        const hotelIds = (codeModal.hotelIds || []).map(Number).filter(n => !Number.isNaN(n));
        const accountId = hotelIds.length ? null : (codeModal.accountId || null);
        // Scope is referenced by syncscope.scopeid (never by name); blank = all scopes.
        const syncScopeIds = (codeModal.syncScopeIds || []).map(Number).filter(n => !Number.isNaN(n));
        // Inactive snippets stay in the editor but the pipeline skips them entirely (V367).
        // 'Y'/'N' — the schema's flag convention, not a boolean or 1/0.
        const active = codeModal.active === false ? 'N' : 'Y';
        try {
            if (codeModal.id) {
                await customCodeService.update(codeModal.id, codeModal.name.trim(), codeModal.code, accountId, hotelIds, syncScopeIds, active);
            } else {
                await customCodeService.create(partnerId, activePlatform.id, codeModal.name.trim(), codeModal.code, accountId, hotelIds, syncScopeIds, active);
            }
            setCodeModal(null);
            await loadCustomCodes(activePlatform);
        } catch (e) {
            // SHOW IT. This was a console.error and nothing else, so a save that failed at the database
            // looked exactly like a save that worked: the modal stayed open, no message appeared, and the
            // list behind it was unchanged — which reads as "the list did not refresh" rather than "the
            // write was rejected". The underlying fault was a read-only connection; the reason it took
            // so long to find was this catch.
            console.error('Failed to save custom code', e);
            setCodeError(e?.response?.data?.message || e?.message || 'Could not save. Please try again.');
        } finally {
            setCodeSaving(false);
        }
    };

    const handleDeleteCode = async (c) => {
        if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
        try {
            await customCodeService.remove(c.id);
            await loadCustomCodes(activePlatform);
        } catch (e) {
            console.error('Failed to delete custom code', e);
        }
    };

    const handleEnterPlatform = async (platform) => {
        setActivePlatform(platform);
        setPlatformDetailTab('templates');
        loadCustomCodes(platform);
        setActiveTemplate(null);
        setTasks([]);
        // Pre-load accounts for share modal
        try {
            const res = await accountService.getAllAccounts({ partnerid: partnerId, activeOnly: true });
            setShareAccounts(res.accounts || res || []);
        } catch { setShareAccounts([]); }
    };

    const handleBackToCards = () => {
        setActivePlatform(null);
        setActiveTemplate(null);
        setTasks([]);
    };

    const handleBackToList = () => {
        setActiveTemplate(null);
        setTasks([]);
    };

    const loadShares = useCallback(async (id) => {
        try {
            const rows = await partnerSyncTemplatesService.listShares(id);
            setShares(prev => ({ ...prev, [id]: rows || [] }));
        } catch {
            setShares(prev => ({ ...prev, [id]: [] }));
        }
    }, []);

    const handleEnterTemplate = (t) => {
        setActiveTemplate(t);
        loadTasks(t.id);
        if (!shares[t.id]) loadShares(t.id);
    };

    const handleToggleExclusion = async (task) => {
        setTogglingTaskId(task.id);
        try {
            if (task.excluded) {
                await partnerSyncTemplatesService.removeExclusion(activeTemplate.id, task.id);
            } else {
                await partnerSyncTemplatesService.addExclusion(activeTemplate.id, task.id);
            }
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, excluded: !t.excluded } : t));
        } catch (err) {
            alert(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to update exclusion');
        } finally {
            setTogglingTaskId(null);
        }
    };

    const handleSetDefault = async (id) => {
        setSettingDefaultId(id);
        setNotice(null);
        try {
            await partnerSyncTemplatesService.setDefault(id);
            await refreshPartnerTemplates();
            // Keep activeTemplate in sync if we're on level 3
            if (activeTemplate && activeTemplate.id === id) {
                setActiveTemplate(prev => ({ ...prev, isDefault: Number(prev.isDefault) === 1 ? 0 : 1 }));
            }
        } catch (err) {
            // 409 = "default and shared are mutually exclusive". The server's message explains which
            // shares are in the way and what to do, so it goes in a banner the reader can keep on screen
            // rather than an alert they have to dismiss before acting on it.
            setNotice(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to set default');
        } finally {
            setSettingDefaultId(null);
        }
    };

    const openShareModal = (templateId) => {
        setShareModal({ templateId });
        setShareType('account');
        setShareAccountPick(null);
        setShareHotelPick(null);
        setShareChainSearch('');
        setShareHotelSearch('');
        setShareHotels([]);
    };

    const handleSelectChain = async (account) => {
        setShareAccountPick({ id: account.ACCOUNTID || account.id, name: account.COMPANYNAME || account.name });
        setShareChainSearch('');
        setShareHotelPick(null);
        setShareHotelSearch('');
        setShareHotels([]);
        if (shareType === 'hotel') {
            setShareHotelsLoading(true);
            try {
                const res = await hotelService.getAllHotels({ accountid: account.ACCOUNTID || account.id });
                setShareHotels(res.hotels || res || []);
            } catch { setShareHotels([]); }
            finally { setShareHotelsLoading(false); }
        }
    };

    const handleShareSave = async () => {
        if (!shareModal) return;
        const accountId = shareType === 'account' ? shareAccountPick?.id : null;
        const hotelId   = shareType === 'hotel'   ? shareHotelPick?.id   : null;
        if (!accountId && !hotelId) return;
        setShareSaving(true);
        setNotice(null);
        try {
            const updated = await partnerSyncTemplatesService.addShare(shareModal.templateId, accountId || undefined, hotelId || undefined);
            setShares(prev => ({ ...prev, [shareModal.templateId]: updated }));
            setShareModal(null);
        } catch (err) {
            // Same conflict from the other direction — sharing a template that is the partner default.
            setShareModal(null);
            setNotice(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to add share');
        } finally {
            setShareSaving(false);
        }
    };

    const handleRemoveShare = async (templateId, shareId) => {
        try {
            await partnerSyncTemplatesService.removeShare(templateId, shareId);
            setShares(prev => ({ ...prev, [templateId]: (prev[templateId] || []).filter(s => s.id !== shareId) }));
        } catch (err) {
            alert(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to remove share');
        }
    };

    const handleModalSave = async () => {
        // Same reasoning as handleSaveCode: accept the click and name the missing field, rather than
        // greying the button out and leaving the reader to work out which of the inputs is at fault.
        if (!modalName.trim()) {
            setModalError('Give the template a name before saving.');
            if (modalNameRef.current) modalNameRef.current.focus();
            return;
        }
        setModalError(null);
        setModalSaving(true);
        try {
            if (modalMode === 'new') {
                await partnerSyncTemplatesService.create(partnerId, activePlatform.id, modalName.trim());
                await refreshPartnerTemplates();
                setModalMode(null);
            } else {
                await partnerSyncTemplatesService.update(modalMode, modalName.trim());
                const updated = modalName.trim();
                setPartnerTemplates(prev => prev.map(t => t.id === modalMode ? { ...t, name: updated } : t));
                if (activeTemplate?.id === modalMode) setActiveTemplate(prev => ({ ...prev, name: updated }));
                setModalMode(null);
            }
        } catch (err) {
            alert(err.response?.data?.error?.message || err.response?.data?.message || 'Save failed');
        } finally {
            setModalSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await partnerSyncTemplatesService.remove(deleteId);
            setPartnerTemplates(prev => prev.filter(t => t.id !== deleteId));
            if (activeTemplate?.id === deleteId) handleBackToList();
        } catch (err) {
            alert(err.response?.data?.error?.message || err.response?.data?.message || 'Delete failed');
        } finally {
            setDeleteId(null);
        }
    };

    if (!partnerId) return <div className="pst-empty">No partner selected.</div>;
    if (loading) return <div className="pst-loading">Loading...</div>;
    if (error) return <div className="pst-error">{error}</div>;

    // Rendered by every view below (the component returns from three places), so a refusal is visible
    // wherever the user was when they triggered it.
    const NoticeBanner = () => notice ? (
        <div className="pst-notice">
            <span className="pst-notice-text">{notice}</span>
            <button type="button" className="pst-notice-close" onClick={() => setNotice(null)} title="Dismiss">×</button>
        </div>
    ) : null;

    // ── Cards view ────────────────────────────────────────────────────────────

    if (!activePlatform) {
        return (
            <div className="pst-container">
                <NoticeBanner />
                <div className="pst-cards-header">
                    <h3>Sync Templates</h3>
                    <p className="pst-subtitle">
                        Select a sync template to create partner-specific configurations and manage task exclusions.
                    </p>
                </div>
                <div className="pst-cards-grid">
                    {platformTemplates.map(p => (
                        <PlatformCard
                            key={p.id}
                            platform={p}
                            partnerTemplates={partnerTemplates}
                            onClick={() => handleEnterPlatform(p)}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // ── Level 2: list of partner templates for this platform ─────────────────

    const platformPartnerTemplates = partnerTemplates.filter(t => t.synctemplatesid === activePlatform.id);

    if (!activeTemplate) {
        const BackArrow = () => (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        );
        // Custom code add/edit — full-screen form (no modals, per STYLE.md).
        if (codeModal !== null) {
            return (
                <div className="pst-container">
                <NoticeBanner />
                    <div className="pst-detail-header pst-detail-header-stacked">
                        <button className="pst-back-btn" onClick={() => { setCodeError(null); setCodeModal(null); }} disabled={codeSaving}><BackArrow /> Back to Custom Code</button>
                        <div className="pst-detail-header-main">
                            <div className="pst-detail-graphic">
                                <div className="pst-detail-title">{codeModal.id ? 'Edit Code' : 'Add Code'}</div>
                            </div>
                        </div>
                    </div>
                    <div className="pst-code-screen">
                        <div className="pst-form-group">
                            <label>Name *</label>
                            <input ref={codeNameRef} value={codeModal.name} onChange={e => { setCodeError(null); setCodeModal({ ...codeModal, name: e.target.value }); }} placeholder="e.g. Strip empty emails" autoFocus />
                        </div>
                        <div className="pst-form-row">
                            <div className="pst-form-group">
                                {/* Status is a deliberate control, not a checkbox tucked away: an inactive
                                    snippet is invisible to the pipeline, so it must be obvious which state
                                    the snippet is in before someone edits its code and wonders why nothing
                                    changes. */}
                                <label>Status</label>
                                <button
                                    type="button"
                                    className={`pst-active-toggle ${codeModal.active === false ? 'is-off' : 'is-on'}`}
                                    aria-pressed={codeModal.active !== false}
                                    onClick={() => setCodeModal({ ...codeModal, active: codeModal.active === false })}
                                >
                                    <span className="pst-active-dot" />
                                    {codeModal.active === false ? 'Inactive — not run by the sync' : 'Active — runs in the sync'}
                                </button>
                            </div>
                            <div className="pst-form-group">
                                <label>Target Chain</label>
                                <select value={codeModal.accountId || ''} onChange={e => handleCodeChainChange(e.target.value)}>
                                    <option value="">— select chain —</option>
                                    {codeAccounts.map(a => (
                                        <option key={a.ACCOUNTID || a.id} value={a.ACCOUNTID || a.id}>{a.COMPANYNAME || a.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Hotels are a SET, for the same reason scopes are: the same transformation
                                routinely applies to a handful of properties in a chain and not the rest.
                                A single select forced the choice between duplicating the snippet per
                                hotel — n bodies to keep in step — and widening it to the whole chain,
                                where it runs on properties it was never meant for.

                                Checkboxes rather than a native multi-select, matching the scopes control
                                below: a <select multiple> hides the unselected options and needs
                                ctrl-click to combine, which nobody discovers. Searchable because a chain
                                can hold hundreds of properties. */}
                            <div className="pst-form-group pst-scope-group">
                                <label>
                                    Target Hotels (optional — none selected means the whole chain)
                                    {(codeModal.hotelIds || []).length > 0 && (
                                        <span className="pst-hotel-count">{codeModal.hotelIds.length} selected</span>
                                    )}
                                </label>
                                {!codeModal.accountId ? (
                                    <small className="pst-scope-hint">Select a chain first.</small>
                                ) : (
                                    <>
                                        {codeHotels.length > 8 && (
                                            <input
                                                type="text"
                                                className="pst-hotel-search"
                                                placeholder="Search hotels…"
                                                value={codeHotelSearch}
                                                onChange={e => setCodeHotelSearch(e.target.value)}
                                            />
                                        )}
                                        <div className="pst-scope-list pst-hotel-list">
                                            {codeHotels
                                                .filter(h => {
                                                    const q = codeHotelSearch.trim().toLowerCase();
                                                    if (!q) return true;
                                                    const id = String(h.hotelid || h.id);
                                                    // A selected hotel stays visible whatever the search
                                                    // says — hiding a ticked box is how a selection gets
                                                    // removed without anyone meaning to.
                                                    if ((codeModal.hotelIds || []).map(String).includes(id)) return true;
                                                    return String(h.hotelname || h.name || '').toLowerCase().includes(q)
                                                        || String(h.hotelcode || '').toLowerCase().includes(q);
                                                })
                                                .map(h => {
                                                    const id = String(h.hotelid || h.id);
                                                    const checked = (codeModal.hotelIds || []).map(String).includes(id);
                                                    return (
                                                        <label key={id} className={`pst-scope-chip${checked ? ' is-on' : ''}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => {
                                                                    const cur = (codeModal.hotelIds || []).map(String);
                                                                    setCodeModal({
                                                                        ...codeModal,
                                                                        hotelIds: cur.includes(id)
                                                                            ? cur.filter(x => x !== id)
                                                                            : [...cur, id],
                                                                    });
                                                                }}
                                                            />
                                                            {h.hotelname || h.name}
                                                        </label>
                                                    );
                                                })}
                                            {codeHotels.length === 0 && (
                                                <small className="pst-scope-hint">This chain has no hotels.</small>
                                            )}
                                        </div>
                                        {(codeModal.hotelIds || []).length > 0 && (
                                            <button
                                                type="button"
                                                className="pst-hotel-clear"
                                                onClick={() => setCodeModal({ ...codeModal, hotelIds: [] })}
                                            >
                                                Clear — target the whole chain instead
                                            </button>
                                        )}
                                    </>
                                )}
                                {(codeModal.hotelIds || []).length === 0 && codeModal.accountId && (
                                    <small className="pst-scope-hint">Runs for every hotel in the chain.</small>
                                )}
                            </div>
                            {/* Scopes are a SET, not one value: the same transformation often applies to a
                                few scopes but not to all of them. Checkboxes rather than a multi-select
                                <select> — a native multi-select hides the unselected options and needs
                                ctrl-click to combine, which nobody discovers. Selecting none means "runs
                                for every scope", which is what the old empty option meant. */}
                            <div className="pst-form-group pst-scope-group">
                                <label>Scopes (optional — none selected means all)</label>
                                <div className="pst-scope-list">
                                    {codeScopeTypes.map(sc => {
                                        const checked = (codeModal.syncScopeIds || []).some(id => String(id) === String(sc.scopeId));
                                        return (
                                            <label key={sc.scopeId} className={`pst-scope-chip${checked ? ' is-on' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        const cur = (codeModal.syncScopeIds || []).map(String);
                                                        const id = String(sc.scopeId);
                                                        setCodeModal({
                                                            ...codeModal,
                                                            syncScopeIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id],
                                                        });
                                                    }}
                                                />
                                                {sc.scopeType}
                                            </label>
                                        );
                                    })}
                                </div>
                                {(codeModal.syncScopeIds || []).length === 0 && (
                                    <small className="pst-scope-hint">Runs for every scope.</small>
                                )}
                            </div>
                        </div>
                        <div className="pst-form-group">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <label style={{ margin: 0 }}>Code</label>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem' }}
                                    onClick={() => {
                                        if (codeModal.code && codeModal.code.trim() && !window.confirm('Replace the current code with the example?')) return;
                                        setCodeModal({ ...codeModal, code: CUSTOM_CODE_SKELETON });
                                    }}
                                >
                                    Initialize
                                </button>
                            </div>
                            <textarea className="pst-code-editor" value={codeModal.code} onChange={e => setCodeModal({ ...codeModal, code: e.target.value })} spellCheck={false} rows={20} placeholder="// custom code snippet — click Initialize for an example" />
                        </div>

                        {/* Test harness. Collapsed by default so it does not push Save off screen. */}
                        <div className="pst-test">
                            <button type="button" className="pst-test-toggle" onClick={() => setTestOpen(!testOpen)}>
                                <span className={`pst-test-chevron ${testOpen ? 'open' : ''}`}>&#9656;</span>
                                Test against a payload
                            </button>
                            {testOpen && (
                                <CustomCodeTester
                                    code={codeModal.code}
                                    // The pipeline passes the project's partner/chain/hotel; mirrored from the
                                    // snippet's own target. One hotel stands in for "a hotel this runs on" — a
                                    // snippet runs per record, and a record belongs to one property.
                                    context={{
                                        partnerId,
                                        chainId: codeModal.accountId ? Number(codeModal.accountId) : null,
                                        hotelId: (codeModal.hotelIds || []).length ? Number(codeModal.hotelIds[0]) : null,
                                    }}
                                    payload={testPayload}
                                    onPayloadChange={setTestPayload}
                                    placeholder='Paste the record JSON the step would receive, e.g. { "reservations": { "reservation": [ ... ] } }'
                                    failureMeaning="in the pipeline this would put the record into ERROR."
                                />
                            )}
                        </div>
                        {/* Claude assistant. Collapsed by default — it is an aid, not the main control. */}
                        <div className="pst-test">
                            <button type="button" className="pst-test-toggle" onClick={() => setAssistOpen(!assistOpen)}>
                                <span className={`pst-test-chevron ${assistOpen ? 'open' : ''}`}>&#9656;</span>
                                Ask Claude about this snippet
                            </button>
                            {assistOpen && (
                                <div className="pst-test-body">
                                    <CustomCodeAssistant
                                        snippetContext={{
                                            code: codeModal.code || '',
                                            scopeType: (codeModal.syncScopeIds || [])
                                                .map(id => (codeScopeTypes.find(t => String(t.scopeId) === String(id)) || {}).scopeType)
                                                .filter(Boolean).join(', ') || null,
                                            chainName: (codeAccounts.find(a => String(a.ACCOUNTID || a.id) === String(codeModal.accountId)) || {}).COMPANYNAME || null,
                                            hotelName: (codeModal.hotelIds || [])
                                                .map(id => (codeHotels.find(h => String(h.hotelid || h.id) === String(id)) || {}).hotelname)
                                                .filter(Boolean).join(', ') || null,
                                            samplePayload: testPayload || '',
                                        }}
                                        onInsert={(code) => setCodeModal({ ...codeModal, code })}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="pst-modal-actions">
                            {codeError && <div className="pst-save-error" role="alert">{codeError}</div>}
                            <button className="btn btn-secondary" onClick={() => { setCodeError(null); setCodeModal(null); }} disabled={codeSaving}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSaveCode} disabled={codeSaving}>{codeSaving ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="pst-container">
                <NoticeBanner />
                <div className="pst-detail-header pst-detail-header-stacked">
                    <button className="pst-back-btn" onClick={handleBackToCards}><BackArrow /> All Templates</button>
                    <div className="pst-detail-header-main">
                        <div className="pst-detail-graphic">
                            <SyncTemplateGraphic template={activePlatform} compact />
                            <div className="pst-detail-title">{activePlatform.name}</div>
                        </div>
                    </div>
                </div>

                <div className="pst-detail-tabs">
                    <button
                        className={`pst-detail-tab ${platformDetailTab === 'templates' ? 'active' : ''}`}
                        onClick={() => setPlatformDetailTab('templates')}
                    >
                        Partner Templates
                    </button>
                    <button
                        className={`pst-detail-tab ${platformDetailTab === 'custom' ? 'active' : ''}`}
                        onClick={() => setPlatformDetailTab('custom')}
                    >
                        Custom Code
                    </button>
                </div>

                {platformDetailTab === 'templates' && (
                  <>
                    <div className="pst-tab-toolbar">
                        <button className="btn btn-primary btn-sm" onClick={() => { setModalMode('new'); setModalName(''); }}>
                            Add Template
                        </button>
                    </div>
                    {platformPartnerTemplates.length === 0 ? (
                        <div className="pst-empty-list">No templates yet. Click "Add Template" to get started.</div>
                    ) : (
                        <div className="pst-simple-list">
                            {platformPartnerTemplates.map(t => (
                                <div key={t.id} className="pst-simple-row" onClick={() => handleEnterTemplate(t)}>
                                    <div className="pst-simple-row-name">
                                        {t.name}
                                        {Number(t.isDefault) === 1 && <span className="pst-default-badge">Default</span>}
                                    </div>
                                    {Number(t.exclusionCount) > 0 && (
                                        <span className="pst-exclusion-count">{t.exclusionCount} excluded</span>
                                    )}
                                    <svg className="pst-row-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                            ))}
                        </div>
                    )}
                  </>
                )}

                {platformDetailTab === 'custom' && (
                  <>
                    <div className="pst-tab-toolbar">
                        <button className="btn btn-primary btn-sm" onClick={handleAddCode}>
                            Add Code
                        </button>
                    </div>
                    {customCodesLoading ? (
                        <div className="pst-loading">Loading...</div>
                    ) : customCodes.length === 0 ? (
                        <div className="pst-empty-list">No custom code yet. Click "Add Code" to get started.</div>
                    ) : (
                        <div className="pst-simple-list">
                            {customCodes.map(c => (
                                <div key={c.id} className="pst-simple-row" onClick={() => handleEditCode(c)}>
                                    <div className="pst-simple-row-name">
                                        {c.name}
                                        {/* scope_types, not scopetype. The latter is the legacy single-scope
                                            column, which writers have nulled since V409 — so every snippet
                                            saved after that read "All scopes" here however many it named.
                                            scope_types is the child table, which is what actually runs. */}
                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}>
                                            {c.scope_types || c.scopetype || 'All scopes'}
                                        </span>
                                        {/* Where it runs. Worth showing now that a snippet can name several
                                            hotels: without it the only way to tell a three-hotel snippet from
                                            a chain-wide one is to open it. */}
                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}>
                                            · {c.hotel_names || (c.account_id ? 'Whole chain' : 'All chains')}
                                        </span>
                                        {String(c.active || 'Y').toUpperCase() === 'N' && (
                                            <span className="pst-inactive-badge">Inactive</span>
                                        )}
                                    </div>
                                    {/* Grouped so the row reads name — actions — chevron. Without the
                                        wrapper the row's space-between spreads four children evenly and
                                        the two buttons drift apart. */}
                                    <div className="pst-row-actions">
                                        <button className="pst-dup-btn" onClick={(e) => { e.stopPropagation(); handleDuplicateCode(c); }}>Duplicate</button>
                                        <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleDeleteCode(c); }}>Delete</button>
                                    </div>
                                    <svg className="pst-row-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                            ))}
                        </div>
                    )}
                  </>
                )}

                {/* Name modal */}
                {modalMode !== null && (
                    <div className="pst-overlay">
                        <div className="pst-modal">
                            <h3>{modalMode === 'new' ? 'New Template' : 'Rename Template'}</h3>
                            <div className="pst-form-group">
                                <label>Name *</label>
                                <input ref={modalNameRef} value={modalName} onChange={e => { setModalError(null); setModalName(e.target.value); }} placeholder="e.g. Standard Opera migration" autoFocus onKeyDown={e => e.key === 'Enter' && handleModalSave()} />
                            </div>
                            <div className="pst-modal-actions">
                                <button className="btn btn-secondary" onClick={() => setModalMode(null)} disabled={modalSaving}>Cancel</button>
                                {modalError && <div className="pst-save-error" role="alert">{modalError}</div>}
                                <button className="btn btn-primary" onClick={handleModalSave} disabled={modalSaving}>{modalSaving ? 'Saving...' : 'Save'}</button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        );
    }

    // ── Level 3: template detail — exclusions + shares ────────────────────────

    const BackArrow = () => (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
    const templateShares = shares[activeTemplate.id] || [];

    return (
        <div className="pst-container">
                <NoticeBanner />
            {/* Level 3 header */}
            <div className="pst-detail-header">
                <button className="pst-back-btn" onClick={handleBackToList}><BackArrow /> {activePlatform.name}</button>
                <div className="pst-detail-title pst-detail-title-lg">
                    {activeTemplate.name}
                    {Number(activeTemplate.isDefault) === 1 && <span className="pst-default-badge">Default</span>}
                </div>
                <div className="pst-detail-actions">
                    <button
                        className={`btn btn-sm ${Number(activeTemplate.isDefault) === 1 ? 'pst-btn-default-on' : 'btn-secondary'}`}
                        onClick={() => handleSetDefault(activeTemplate.id)}
                        disabled={settingDefaultId === activeTemplate.id}
                        title={Number(activeTemplate.isDefault) === 1 ? 'Click to remove default' : 'Set as default'}
                    >
                        {settingDefaultId === activeTemplate.id ? '…' : Number(activeTemplate.isDefault) === 1 ? 'Default ✓' : 'Set Default'}
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => { setModalMode(activeTemplate.id); setModalName(activeTemplate.name); }}>Rename</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => openShareModal(activeTemplate.id)}>Share</button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteId(activeTemplate.id)}>Delete</button>
                </div>
            </div>

            {/* Shares */}
            {templateShares.length > 0 && (
                <div className="pst-share-chips pst-share-chips-bar">
                    <span className="pst-share-label">Shared with:</span>
                    {templateShares.map(s => (
                        <span key={s.id} className="pst-share-chip">
                            {s.hotelName || s.accountName || `#${s.hotel_id || s.account_id}`}
                            <button className="pst-chip-remove" onClick={() => handleRemoveShare(activeTemplate.id, s.id)} title="Remove share">×</button>
                        </span>
                    ))}
                </div>
            )}

            {/* Task exclusions */}
            <div className="pst-tasks-panel pst-tasks-full">
                <div className="pst-tasks-heading">
                    <span className="pst-tasks-hint">Excluded tasks are hidden from users during a sync project setup.</span>
                </div>
                {tasksLoading ? (
                    <div className="pst-tasks-loading">Loading tasks...</div>
                ) : (
                    <table className="pst-tasks-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Task</th>
                                <th>Phase</th>
                                <th>Target</th>
                                <th className="pst-col-excluded">Excluded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.length === 0 && (
                                <tr><td colSpan={5} className="pst-no-tasks">No tasks found.</td></tr>
                            )}
                            {tasks.map(task => (
                                <tr key={task.id} className={task.excluded ? 'pst-row-excluded' : ''}>
                                    <td className="pst-seq">{task.sequence}</td>
                                    <td>
                                        <div className="pst-task-name">{task.name}</div>
                                        {task.description && <div className="pst-task-desc">{task.description}</div>}
                                    </td>
                                    <td>{task.phase || '—'}</td>
                                    <td>{task.target || '—'}</td>
                                    <td className="pst-col-excluded">
                                        <button
                                            className={`pst-toggle ${task.excluded ? 'pst-toggle-on' : 'pst-toggle-off'}`}
                                            onClick={() => handleToggleExclusion(task)}
                                            disabled={togglingTaskId === task.id}
                                            title={task.excluded ? 'Click to include' : 'Click to exclude'}
                                        >
                                            {task.excluded ? 'Excluded' : 'Included'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Name modal */}
            {modalMode !== null && (
                <div className="pst-overlay">
                    <div className="pst-modal">
                        <h3>{modalMode === 'new' ? 'New Template' : 'Rename Template'}</h3>
                        <div className="pst-form-group">
                            <label>Name *</label>
                            <input
                                value={modalName}
                                onChange={e => setModalName(e.target.value)}
                                placeholder="e.g. Standard Opera migration"
                                autoFocus
                                onKeyDown={e => e.key === 'Enter' && handleModalSave()}
                            />
                        </div>
                        <div className="pst-modal-actions">
                            <button className="btn btn-secondary" onClick={() => setModalMode(null)} disabled={modalSaving}>Cancel</button>
                            {modalError && <div className="pst-save-error" role="alert">{modalError}</div>}
                                <button className="btn btn-primary" onClick={handleModalSave} disabled={modalSaving}>
                                {modalSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share modal */}
            {shareModal && (() => {
                const chainQ = shareChainSearch.toLowerCase();
                const filteredChains = chainQ
                    ? shareAccounts.filter(a => (a.COMPANYNAME || a.name || '').toLowerCase().includes(chainQ))
                    : shareAccounts;
                const hotelQ = shareHotelSearch.toLowerCase();
                const filteredHotels = hotelQ
                    ? shareHotels.filter(h => (h.hotelname || h.name || '').toLowerCase().includes(hotelQ))
                    : shareHotels;
                return (
                    <div className="pst-overlay">
                        <div className="pst-modal">
                            <h3>Share Template</h3>
                            <div className="pst-form-group">
                                <label>Share with</label>
                                <div className="pst-radio-group">
                                    <label className="pst-radio-label">
                                        <input type="radio" value="account" checked={shareType === 'account'} onChange={() => { setShareType('account'); setShareHotelPick(null); setShareHotelSearch(''); }} />
                                        Chain
                                    </label>
                                    <label className="pst-radio-label">
                                        <input type="radio" value="hotel" checked={shareType === 'hotel'} onChange={() => setShareType('hotel')} />
                                        Hotel
                                    </label>
                                </div>
                            </div>

                            <div className="pst-form-group">
                                <label>Chain *</label>
                                {shareAccountPick ? (
                                    <div className="pst-search-selected">
                                        <span>{shareAccountPick.name}</span>
                                        <button className="pst-chip-remove" onClick={() => { setShareAccountPick(null); setShareHotelPick(null); setShareHotels([]); setShareHotelSearch(''); }}>×</button>
                                    </div>
                                ) : (
                                    <div className="pst-search-wrap">
                                        <input
                                            type="text"
                                            value={shareChainSearch}
                                            onChange={e => setShareChainSearch(e.target.value)}
                                            placeholder="Search chains…"
                                            autoFocus
                                        />
                                        {filteredChains.length > 0 && (
                                            <div className="pst-search-results">
                                                {filteredChains.slice(0, 8).map(a => (
                                                    <div key={a.ACCOUNTID || a.id} className="pst-search-result-item" onMouseDown={() => handleSelectChain(a)}>
                                                        {a.COMPANYNAME || a.name}
                                                    </div>
                                                ))}
                                                {filteredChains.length > 8 && (
                                                    <div className="pst-search-result-more">+{filteredChains.length - 8} more — type to filter</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {shareType === 'hotel' && (
                                <div className="pst-form-group">
                                    <label>Hotel *</label>
                                    {shareHotelsLoading ? (
                                        <div className="pst-loading">Loading hotels…</div>
                                    ) : !shareAccountPick ? (
                                        <div className="pst-search-hint">Select a chain first</div>
                                    ) : shareHotelPick ? (
                                        <div className="pst-search-selected">
                                            <span>{shareHotelPick.name}</span>
                                            <button className="pst-chip-remove" onClick={() => { setShareHotelPick(null); setShareHotelSearch(''); }}>×</button>
                                        </div>
                                    ) : (
                                        <div className="pst-search-wrap">
                                            <input
                                                type="text"
                                                value={shareHotelSearch}
                                                onChange={e => setShareHotelSearch(e.target.value)}
                                                placeholder="Search hotels…"
                                            />
                                            {filteredHotels.length > 0 && (
                                                <div className="pst-search-results">
                                                    {filteredHotels.slice(0, 8).map(h => (
                                                        <div key={h.hotelid || h.id} className="pst-search-result-item" onMouseDown={() => { setShareHotelPick({ id: h.hotelid || h.id, name: h.hotelname || h.name }); setShareHotelSearch(''); }}>
                                                            {h.hotelname || h.name}
                                                        </div>
                                                    ))}
                                                    {filteredHotels.length > 8 && (
                                                        <div className="pst-search-result-more">+{filteredHotels.length - 8} more — type to filter</div>
                                                    )}
                                                </div>
                                            )}
                                            {shareHotels.length === 0 && !shareHotelsLoading && (
                                                <div className="pst-search-hint">No hotels found for this chain</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="pst-modal-actions">
                                <button className="btn btn-secondary" onClick={() => setShareModal(null)} disabled={shareSaving}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleShareSave}
                                    disabled={shareSaving || (shareType === 'account' ? !shareAccountPick : !shareHotelPick)}
                                >
                                    {shareSaving ? 'Saving…' : 'Share'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Delete confirm */}
            {deleteId && (
                <div className="pst-overlay">
                    <div className="pst-modal pst-modal-sm">
                        <h3>Delete Template</h3>
                        <p>Are you sure? This will also remove all task exclusions for this template.</p>
                        <div className="pst-modal-actions">
                            <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Cancel</button>
                            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartnerSyncTemplates;
